import { httpBare } from '@/api/httpBare'
import { getAccessToken } from '@/utils/accessToken'
import { applyLoginOrRefreshPayload } from '@/utils/authPayload'

/** 与后端约定的刷新接口（Cookie 携带 Refresh Token，勿在 Body 传 Refresh） */
export const AUTH_REFRESH_PATH = '/api/app/core/user/refresh'

let refreshPromise = null
let lastRefreshFailure = null

const TRANSIENT_RETRY_DELAYS_MS = [400, 1000]

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function isDefinitiveAuthFailure(error) {
  const response = error && error.response
  const httpStatus = response && Number(response.status)
  const body = response && response.data
  const businessCode = body && typeof body === 'object' ? Number(body.code) : NaN
  return httpStatus === 401 || businessCode === 401
}

/**
 * 最近一次刷新是否由服务端明确判定 Token 无效。
 * 网络中断、超时和 5xx 均返回 false，不能据此清空持久登录状态。
 */
export function wasLastRefreshRejected() {
  return lastRefreshFailure === 'rejected'
}

/**
 * 使用 HttpOnly Cookie 中的 Refresh Token 换取新的 Access Token（及可选用户信息）。
 * 供路由开屏、401 重试、手动调用。
 * @param {boolean} [force=false] 即使内存中已有 Access Token 也执行刷新
 * @returns {Promise<boolean>}
 */
export async function tryRefreshFromCookie(force = false) {
  if (!force && getAccessToken()) {
    lastRefreshFailure = null
    return true
  }

  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    for (let attempt = 0; attempt <= TRANSIENT_RETRY_DELAYS_MS.length; attempt += 1) {
      try {
        const { data } = await httpBare.post(AUTH_REFRESH_PATH, {})
        applyLoginOrRefreshPayload(data)
        lastRefreshFailure = null
        return true
      } catch (e) {
        if (isDefinitiveAuthFailure(e)) {
          lastRefreshFailure = 'rejected'
          return false
        }
        if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
          await sleep(TRANSIENT_RETRY_DELAYS_MS[attempt])
          continue
        }
        lastRefreshFailure = 'transient'
        console.warn('会话恢复暂时失败，已保留登录状态，可稍后重试', e)
        return false
      }
    }
    return false
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

/**
 * 确保内存中有 Access Token（无则尝试 Cookie 刷新）。
 */
export async function ensureAccessToken() {
  return tryRefreshFromCookie()
}

/** Access 临近过期时主动轮换，不能因内存中仍有旧 Token 而跳过刷新。 */
export async function refreshAccessTokenFromCookie() {
  return tryRefreshFromCookie(true)
}
